/**
 * SquareDashboard — v3 (refactored)
 *
 * State-management and routing only.
 * All UI is delegated to ./components/.
 * Shared utilities in ../../utils/ and ./squareDashboardUtils.js.
 *
 * Tabs: Overview · Categories · All Items · Tickets · Returns
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import { TZ, toCST_ISO } from '../../utils/cstUtils';
import { fmtN } from '../../utils/formatUtils';
import {
  API_BASE,
  computeRange,
  isQuickPreset,
  buildRangeLabel,
  getTicketType,
  TICKET_TYPE_META,
  toLocalInput,
} from './squareDashboardUtils';

import AITab         from './components/AITab';
import CompareTab    from './components/CompareTab';
import GrowthTab     from './components/GrowthTab';
import WeatherTab    from './components/WeatherTab';
import TopBar        from './components/TopBar';
import KpiSection    from './components/KpiSection';
import OverviewTab   from './components/OverviewTab';
import CategoriesTab from './components/CategoriesTab';
import AllItemsTab   from './components/AllItemsTab';
import TicketsTab    from './components/TicketsTab';
import ReturnsTab    from './components/ReturnsTab';

import './SquareDashboard.css';

const ALLOWED_ROLES = ['owner'];

// ── Route guard ────────────────────────────────────────────────────────────────
export default function SquareDashboard() {
  const { role } = useAuth();
  if (!ALLOWED_ROLES.includes(role)) return <Navigate to="/" replace />;
  return <SquareDashboardInner />;
}

// ── Main dashboard ─────────────────────────────────────────────────────────────
function SquareDashboardInner() {
  const [preset,       setPreset]       = useState('m20');
  const [customStart,  setCustomStart]  = useState('');
  const [customEnd,    setCustomEnd]    = useState('');
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [activeTab,    setActiveTab]    = useState('overview');
  const [selectedCat,  setSelectedCat]  = useState(null);
  const [showKpis,     setShowKpis]     = useState(true);
  const [showDateEdit, setShowDateEdit] = useState(false);
  const [aiOpenCount,  setAiOpenCount]  = useState(0);  // live badge from AITab

  // Seed custom inputs to today on mount
  useEffect(() => {
    const { start, end } = computeRange('today');
    setCustomStart(toLocalInput(start));
    setCustomEnd(toLocalInput(end));
  }, []);

  const isQuick = isQuickPreset(preset);

  // Resolve start/end Date objects
  const { startDt, endDt } = useMemo(() => {
    if (preset === 'custom') {
      const s = customStart ? new Date(customStart) : computeRange('today').start;
      const e = customEnd   ? new Date(customEnd)   : computeRange('today').end;
      return { startDt: s, endDt: e };
    }
    const { start, end } = computeRange(preset);
    return { startDt: start, endDt: end };
  }, [preset, customStart, customEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch sales data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let startISO, endISO;
      if (isQuick) {
        const { start: qs, end: qe } = computeRange(preset);
        startISO = toCST_ISO(qs);
        endISO   = toCST_ISO(qe);
      } else {
        startISO = toCST_ISO(startDt);
        endISO   = toCST_ISO(endDt);
      }
      const url = `${API_BASE}/api/square-sales?startAt=${encodeURIComponent(startISO)}&endAt=${encodeURIComponent(endISO)}`;
      const res  = await fetch(url);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Unknown error');
      setData(json);
      setSelectedCat(null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [startDt.getTime(), endDt.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  // Derived: range label
  const rangeLabel = useMemo(
    () => buildRangeLabel(preset, startDt, endDt, TZ),
    [preset, startDt, endDt]
  );

  // Derived: ticket type breakdown
  const typeBreakdown = useMemo(() => {
    if (!data) return {};
    const result = {};
    Object.keys(TICKET_TYPE_META).forEach(k => {
      result[k] = { count: 0, revenueCents: 0, discountCents: 0 };
    });
    data.categories.forEach(cat => {
      (cat.ticketList || []).forEach(tkt => {
        const t = getTicketType(tkt);
        if (result[t]) {
          result[t].count++;
          result[t].revenueCents  += tkt.revenueCents  || 0;
          result[t].discountCents += tkt.discountCents || 0;
        }
      });
    });
    return result;
  }, [data]);

  // Derived: returns roll-up
  const returnsData = useMemo(() => {
    if (!data) return [];
    const map = {};
    data.categories.forEach(cat => {
      cat.items.forEach(item => {
        if (item.returnQty > 0) {
          const k = item.name.toLowerCase();
          if (!map[k]) map[k] = { name: item.name, returnQty: 0, returnRevenue: 0, categories: [] };
          map[k].returnQty     += item.returnQty;
          map[k].returnRevenue += parseFloat(item.returnRevenue || 0);
          if (!map[k].categories.includes(cat.name)) map[k].categories.push(cat.name);
        }
      });
    });
    return Object.values(map).sort((a, b) => b.returnQty - a.returnQty);
  }, [data]);

  // Derived: max category revenue (for bar scaling)
  const maxCatRevenue = useMemo(
    () => (data && data.categories.length
      ? Math.max(...data.categories.map(c => c.revenueCents), 1)
      : 1),
    [data]
  );

  // Navigation helper: jump to a specific category
  const goToCategory = useCallback((catName) => {
    setActiveTab('categories');
    setSelectedCat(catName);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="sq__page">
      <TopBar
        preset={preset}           setPreset={setPreset}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd}     setCustomEnd={setCustomEnd}
        loading={loading}
        showDateEdit={showDateEdit} setShowDateEdit={setShowDateEdit}
        rangeLabel={rangeLabel}
        fetchData={fetchData}
      />

      {/* AI Intelligence — always available, independent of date-range data */}
      <div className="sq__ai-always-bar">
        <button
          className={`sq__navtab sq__navtab--ai sq__navtab--ai-always${activeTab === 'ai' ? ' active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          🤖 AI Intelligence
          {aiOpenCount > 0 && (
            <span className={`sq__ai-badge${aiOpenCount >= 7 ? ' sq__ai-badge--alert' : ''}`}>
              {aiOpenCount} open
            </span>
          )}
        </button>
        <span className="sq__ai-tagline">Live ticket monitor · 30s refresh · SMS &amp; push alerts</span>
      </div>

      {activeTab === 'ai' && <AITab onOpenCountChange={setAiOpenCount} />}

      <div className="sq__body">
        {loading && (
          <div className="sq__loading">
            <span className="sq__spinner" />
            <span>Loading sales data…</span>
          </div>
        )}

        {error && !loading && (
          <div className="sq__error-bar">
            <span>⚠️ {error}</span>
            <button onClick={fetchData}>Retry</button>
          </div>
        )}

        {data && !loading && (
          <>
            {/* Collapsible metrics toggle */}
            <div className="sq__metrics-bar">
              <button
                className="sq__metrics-toggle"
                onClick={() => setShowKpis(v => !v)}
              >
                {showKpis ? '▴ Hide Metrics' : '▾ Show Metrics'}
                <span className="sq__metrics-peek">
                  {fmtN(data.summary.totalTickets)} tickets &nbsp;·&nbsp;
                  {fmtN(data.categories.reduce((s, c) => s + c.itemQty, 0))} items
                  {data.summary.totalReturnQty > 0 && (
                    <span className="sq__metrics-ret">
                      &nbsp;·&nbsp; ↩ {data.summary.totalReturnQty} returns
                    </span>
                  )}
                </span>
              </button>
            </div>

            <KpiSection
              data={data}
              showKpis={showKpis}
              typeBreakdown={typeBreakdown}
            />

            {/* Tab navigation */}
            <div className="sq__navtabs">
              <button
                className={`sq__navtab${activeTab === 'overview' ? ' active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                📊 Overview
              </button>
              <button
                className={`sq__navtab${activeTab === 'categories' ? ' active' : ''}`}
                onClick={() => setActiveTab('categories')}
              >
                📁 Categories ({data.categories.length})
              </button>
              <button
                className={`sq__navtab${activeTab === 'allitems' ? ' active' : ''}`}
                onClick={() => setActiveTab('allitems')}
              >
                📦 All Items ({data.categories.reduce((s, c) => s + c.items.length, 0)})
              </button>
              <button
                className={`sq__navtab${activeTab === 'tickets' ? ' active' : ''}`}
                onClick={() => setActiveTab('tickets')}
              >
                🎫 Tickets ({data.summary.totalTickets})
              </button>
              <button
                className={`sq__navtab${activeTab === 'returns' ? ' active' : ''}${data.summary.totalReturnQty > 0 ? ' sq__navtab--alert' : ''}`}
                onClick={() => setActiveTab('returns')}
              >
                ↩️ Returns{data.summary.totalReturnQty > 0 ? ` (${data.summary.totalReturnQty})` : ''}
              </button>
              <button
                className={`sq__navtab sq__navtab--compare${activeTab === 'compare' ? ' active' : ''}`}
                onClick={() => setActiveTab('compare')}
              >
                🆚 Compare
              </button>
              <button
                className={`sq__navtab sq__navtab--growth${activeTab === 'growth' ? ' active' : ''}`}
                onClick={() => setActiveTab('growth')}
              >
                📈 Growth
              </button>
              <button
                className={`sq__navtab sq__navtab--weather${activeTab === 'weather' ? ' active' : ''}`}
                onClick={() => setActiveTab('weather')}
              >
                🌤️ Weather &amp; Forecast
              </button>
            </div>

            {/* Tab panels */}
            {activeTab === 'overview'    && (
              <OverviewTab
                data={data}
                maxCatRevenue={maxCatRevenue}
                goToCategory={goToCategory}
              />
            )}
            {activeTab === 'categories'  && (
              <CategoriesTab
                data={data}
                maxCatRevenue={maxCatRevenue}
                selectedCat={selectedCat}
                setSelectedCat={setSelectedCat}
              />
            )}
            {activeTab === 'allitems'    && <AllItemsTab data={data} />}
            {activeTab === 'tickets'     && <TicketsTab  data={data} />}
            {activeTab === 'compare'     && <CompareTab />}
            {activeTab === 'growth'      && <GrowthTab />}
            {activeTab === 'weather'     && <WeatherTab />}
            {activeTab === 'returns'     && (
              <ReturnsTab
                data={data}
                returnsData={returnsData}
                goToCategory={goToCategory}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
